import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Button,
  Divider,
  Grid,
  Paper,
  Toolbar,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import EmailIcon from '@material-ui/icons/Email';
import PhoneIcon from '@material-ui/icons/Phone';
import { Link } from 'react-router-dom';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import RestaurantServicesIcons from './RestaurantServicesIcons';
import styles from '../constants/styles';
import noAvailablePicture from '../../../assets/utils/NopictureAvailable.png';
import 'react-image-gallery/styles/css/image-gallery.css';
import LearningPlansList from './LearningPlansList';
import { useTranslation } from 'react-i18next';
import {
  getRestaurantAttributes,
  getRestaurantServices,
} from '../../../redux/actions/restaurantsActions';
import { textToSnakeCase } from '../../../utils/utils';

const RestaurantDetailsTab = () => {
  const { images } = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.entity,
  );
  const { thisUser } = useSelector(
    (state: RootStateOrAny) => state.UserReducer,
  );
  const restaurant = useSelector(
    (state: RootStateOrAny) => state.RestaurantsReducer.entity,
  );
  const attributes = useSelector(
    (state: RootStateOrAny) => state.AttributesReducer.entities || [],
  );
  const services = useSelector((state: RootStateOrAny) => state.ServicesReducer.entities || []);
  const classes = styles();

  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState<string>(noAvailablePicture);
  const dispatch: any = useDispatch();

  useEffect(() => {
    if (images?.length > 0) {
      setImageUrl(images[0]);
    }
  }, [images]);

  useEffect(() => {
    if (!restaurant.restaurant_id) {
      return;
    }

    dispatch(getRestaurantAttributes(restaurant.restaurant_id));
    dispatch(getRestaurantServices(restaurant.restaurant_id));
  }, [restaurant]);

  function errorFunction() {
    setImageUrl(noAvailablePicture);
  }

  return (
    <div>
      <Grid
        id="ContainerGlobal"
        container
        spacing={4}
        justify="space-between"
        className={classes.fullWidth}
      >
        <Grid
          id="ContainerSectionGeneralInformation"
          item
          lg={9}
          md={8}
          sm={8}
          xs={12}
          style={{ paddingLeft: 20 }}
        >
          <Grid
            id="ContainerImageAndInformation"
            container
            spacing={1}
            direction="row"
            justify="space-between"
            style={{ paddingBottom: 20 }}
          >
            <Grid id="Image" item xs={12} md={8} sm={10} lg={5} xl={5}>
              {images && (
                <img
                  src={imageUrl}
                  onError={errorFunction}
                  className={classes.imageStyle}
                />
              )}
            </Grid>
            <Grid
              id="ContainerInformation"
              item
              lg={7}
              md={8}
              sm={8}
              xs={12}
              style={{ paddingLeft: 20 }}
            >
              <div className={classes.flexSpaceBetween}>
                <Typography className={classes.boldTitle}>
                  {t('restaurant.info.restaurantInformation')}
                </Typography>

                <Grid item xs={4} sm={3}>
                  {thisUser.hasEditRestaurant && (
                    <Button
                      component={Link}
                      to={`/restaurants/${restaurant.restaurant_id}/edit`}
                      variant="outlined"
                      className={classes.gridEditButton}
                    >
                      <EditIcon />
                    </Button>
                  )}
                </Grid>
              </div>
              <Grid container spacing={1}>
                <Grid item lg={6} className={classes.textAlignRight}>
                  {t('restaurant.info.restaurantNumber')}:
                </Grid>
                <Grid item lg={6} className={classes.textAlignLeft}>
                  {restaurant.RestaurantNumber}
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid item lg={6} className={classes.textAlignRight}>
                  {t('restaurant.info.restaurantAddress')}:
                </Grid>
                <Grid item lg={6} className={classes.textAlignLeft}>
                  {restaurant.RestaurantAddressLine1}{' '}
                  {restaurant.RestaurantAddressLine2}
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid item lg={6} className={classes.textAlignRight}>
                  {t('restaurant.info.landmark')}:
                </Grid>
                <Grid item lg={6} className={classes.textAlignLeft}>
                  {restaurant.Landmark}
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid item lg={6} className={classes.textAlignRight}>
                  {t('restaurant.info.crossStreet')}:
                </Grid>
                <Grid item lg={6} className={classes.textAlignLeft}>
                  {restaurant.Crosstreet}
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid item lg={6} className={classes.textAlignRight}>
                  {t('restaurant.info.timezone')}:
                </Grid>
                <Grid item lg={6} className={classes.textAlignLeft}>
                  {restaurant.TimeZone}
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid item lg={6} className={classes.textAlignRight}>
                  {t('restaurant.info.geolocation')}:
                </Grid>
                <Grid item lg={6} className={classes.textAlignLeft}>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`https://www.google.com/maps/?q=${restaurant.Latitude},${restaurant.Longitude}`}
                  >
                    {restaurant.Latitude}/{restaurant.Longitude}
                  </a>
                </Grid>
              </Grid>
              <Grid />
              <Divider variant="middle" className={classes.divider} />
              <div className={classes.flexCenter}>
                <div className={classes.textFlexCenter}>
                  <Typography>{restaurant.Region}</Typography>
                  <Typography variant="caption">
                    {t('restaurant.info.region')}:
                  </Typography>
                </div>
                <div className={classes.textFlexCenter}>
                  <Typography>
                    {t(
                      `app.common.countries.${textToSnakeCase(restaurant.Country)}`,
                      restaurant.Country,
                    )}
                  </Typography>
                  <Typography variant="caption">
                    {t('restaurant.info.country')}:
                  </Typography>
                </div>
                <div className={classes.textFlexCenter}>
                  <Typography>{restaurant['State/Province']}</Typography>
                  <Typography variant="caption">
                    {t('restaurant.info.state')}:
                  </Typography>
                </div>
                <div className={classes.textFlexCenter}>
                  <Typography>{restaurant.RestaurantAddressCity}</Typography>
                  <Typography variant="caption">
                    {t('restaurant.info.city')}:
                  </Typography>
                </div>
              </div>
            </Grid>
          </Grid>

          <Grid id="ContainerContact">
            <Typography
              className={classes.boldTitle}
              style={{ paddingBottom: 20 }}
            >
              {t('restaurant.info.contactInformation')}
            </Typography>
            <Grid
              container
              spacing={2}
              justify="center"
              alignItems="flex-start"
            >
              <Grid item lg={6} xs={12} className={classes.gridEmailPhone}>
                <Paper
                  elevation={2}
                  className={classes.gridEmailPhone}
                  variant="outlined"
                >
                  <AppBar
                    position="static"
                    color="secondary"
                    className={classes.appBar}
                  >
                    <Toolbar>
                      <Typography className={classes.appbarStyle}>
                        {t('restaurant.info.phones')}:
                      </Typography>
                    </Toolbar>
                  </AppBar>

                  {restaurant.phones?.map((phone, index) => (
                    <Grid
                      container
                      spacing={1}
                      key={index}
                      style={{ margin: 5 }}
                    >
                      <Grid item lg={5} xs={6}>
                        <div className={classes.flexStart}>
                          <PhoneIcon />
                          <Typography
                            style={{ paddingLeft: 10 }}
                            className={classes.appbarStyle}
                          >
                            {t(
                              `app.common.phone_types.${textToSnakeCase(phone.RestaurantPhoneType)}`,
                              phone.RestaurantPhoneType
                            )}
                          </Typography>
                        </div>
                      </Grid>
                      <Grid item lg={4} xs={5}>
                        <div
                          key={`email_${phone.RestaurantPhoneNumberId}`}
                          className={classes.textIcon}
                        >
                          <Typography>{phone.RestaurantPhoneNumber}</Typography>
                        </div>
                      </Grid>
                      <Grid item lg={2} xs={2}>
                        <div key={`Ext_${phone.RestaurantPhoneNumberId}`}>
                          <Grid
                            container
                            direction="row"
                            justify="center"
                            alignItems="flex-end"
                          >
                            <Grid>
                              <Typography>
                                <small>{t('restaurant.info.ext')}</small>
                              </Typography>
                            </Grid>
                            &nbsp;
                            <Grid>
                              <Typography>
                                <small>({phone.PhoneExtension})</small>
                              </Typography>
                            </Grid>
                          </Grid>
                        </div>
                      </Grid>
                    </Grid>
                  ))}
                </Paper>
              </Grid>
              <Grid item lg={6} xs={12} className={classes.gridEmailPhone}>
                <Paper
                  elevation={2}
                  className={classes.gridEmailPhone}
                  variant="outlined"
                  color="secondary"
                >
                  <AppBar
                    position="static"
                    color="secondary"
                    className={classes.appBar}
                  >
                    <Toolbar>
                      <Typography className={classes.appbarStyle}>
                        {t('restaurant.info.emails')}:
                      </Typography>
                    </Toolbar>
                  </AppBar>
                  <Grid container spacing={1} className={classes.emailContainer}>
                    <Grid item lg={5} xs={5}>
                      {restaurant.emails?.map((email, index) => (
                        <div key={index} className={classes.flexStart}>
                          <EmailIcon />
                          <Typography
                            style={{ paddingLeft: 10 }}
                            className={classes.appbarStyle}
                          >
                            {t(
                              `app.common.email_type.${textToSnakeCase(email.RestaurantEmailType)}`,
                              email.RestaurantEmailType
                            )}
                          </Typography>
                        </div>
                      ))}
                    </Grid>
                    <Grid item lg={7} xs={7}>
                      {restaurant.emails?.map(email => (
                        <div
                          key={`email_${email.RestaurantEmailId}`}
                          className={classes.textIcon}
                        >
                          <Typography>{email.RestaurantEmail}</Typography>
                        </div>
                      ))}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          <Divider variant="middle" className={classes.divider} />
          <Grid id="ContainerLearning" style={{ paddingBottom: 20 }}>
            <Typography
              className={classes.title}
              style={{ display: 'block', margin: '1em 0' }}
            >
              {t('restaurant.info.edit.learningPlans')}
            </Typography>
            <LearningPlansList attributes={attributes} />
          </Grid>
        </Grid>

        <Grid
          id="ContainerSectionServicesInformation"
          item
          lg={3}
          md={4}
          sm={4}
          xs={12}
          className={classes.flexStart}
        >
          <Paper elevation={0}>
            <div>
              <Typography className={classes.boldTitle}>
                {t('restaurant.info.restaurantServices')}
              </Typography>
            </div>
            <RestaurantServicesIcons services={services} />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default RestaurantDetailsTab;
